import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import { Input, Button } from '@nextui-org/react';
import { Tasks, AskerAddress } from 'common/src/utils/types';
import {
  fetchAskerByEmail,
  fetchAskerAddresses,
  createAskerAddress,
  createAskerProfile,
  getAskerById,
} from 'common/src/api/askers';
import AskerAddressComponent, {
  AskerAddressComponentRef,
} from './AskerAddressComponent';

import { toast } from 'react-toastify';

// Add this type definition
interface NewAddressData {
  asker_id: number;
  address: string;
  commune: string;
  city: string;
}

interface UserInfoColumnProps {
  formData: Partial<Tasks>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Tasks>>>;
  task?: Tasks;
}

export interface UserInfoColumnRef {
  createNewAddressIfNeeded: () => Promise<void>;
  getNewAddressData: () => NewAddressData | null;
  getUpdatedFormData: () => Partial<Tasks>;
}

const UserInfoColumn = forwardRef<UserInfoColumnRef, UserInfoColumnProps>(
  ({ formData, setFormData, task }, ref) => {
    const [askerAddresses, setAskerAddresses] = useState<AskerAddress[]>([]);
    const [isEmailValidated, setIsEmailValidated] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const askerAddressComponentRef = useRef<AskerAddressComponentRef>(null);

    useEffect(() => {
      if (task) {
        setFormData(task);
        setIsEmailValidated(true);
        loadTaskData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task]);

    const loadTaskData = async () => {
      if (formData.asker_id) {
        try {
          const askerData = await getAskerById(formData.asker_id);
          if (askerData) {
            setFormData(prev => ({
              ...prev,
              email: askerData.email,
              name: askerData.name,
              last_name: askerData.last_name,
              phone_number: askerData.phone_number
            }));
            const addresses = await fetchAskerAddresses(askerData.id);
            setAskerAddresses(addresses || []);
          }
        } catch (error) {
          console.error('Error fetching asker data:', error);
          toast.error('Error al cargar los datos del asker');
        }
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const email = e.target.value;
      setFormData((prev) => ({ ...prev, email }));
      setIsEmailValidated(false);
    };

    const verifyEmail = async () => {
      if (!formData.email) return;

      setIsVerifying(true);
      try {
        let askerData = await fetchAskerByEmail(formData.email);
        if (!askerData) {
          // Create a new asker if the email doesn't exist
          askerData = await createAskerProfile({ email: formData.email });
        }

        setIsEmailValidated(true);
        setFormData((prev) => ({
          ...prev,
          name: askerData.name || '',
          last_name: askerData.last_name || '',
          phone_number: askerData.phone_number || '',
          asker_id: askerData.id,
        }));
        const addresses = await fetchAskerAddresses(askerData.id);
        setAskerAddresses(addresses || []);

        // Add this line to show a success toast in Spanish
        toast.success('Email verificado con éxito');
      } catch (error) {
        console.error('Error validating email:', error);
        toast.error(
          'Error al validar el Email. Por favor, inténtelo de nuevo.'
        );
      } finally {
        setIsVerifying(false);
      }
    };

    const handleAddressChange = (addressId: number | null) => {
      setFormData((prev) => ({
        ...prev,
        askers_addresses_id: addressId,
      }));
    };

    const createNewAddressIfNeeded = async () => {
      if (formData.askers_addresses_id) return; // Address already selected

      if (!formData.asker_id) {
        console.error('No asker_id available. Cannot create address.');
        toast.error('Error: No user selected. Please verify email first.');
        return;
      }

      const newAddressData =
        askerAddressComponentRef.current?.getNewAddressData();
      if (newAddressData) {
        try {
          const newAddress = await createAskerAddress({
            ...newAddressData,
            askerId: formData.asker_id,
          });
          setFormData((prev) => ({
            ...prev,
            askers_addresses_id: newAddress.id,
          }));
          setAskerAddresses((prev) => [...prev, newAddress]);
        } catch (error) {
          console.error('Error creating new address:', error);
          toast.error('Failed to create new address. Please try again.');
        }
      }
    };

    const getNewAddressData = (): NewAddressData | null => {
      return askerAddressComponentRef.current?.getNewAddressData() || null;
    };

    const getUpdatedFormData = (): Partial<Tasks> => {
      return {
        ...formData,
        askers_addresses_id:
          askerAddressComponentRef.current?.getSelectedAddressId() ||
          formData.askers_addresses_id,
      };
    };

    useImperativeHandle(ref, () => ({
      createNewAddressIfNeeded,
      getNewAddressData,
      getUpdatedFormData,
    }));

    return (
      <div className="flex-1 pr-4 flex flex-col gap-4">
        <p className="text-semibold text-xl">Datos del Asker</p>
        <div className="flex gap-2">
          <Input
            label="Email"
            name="email"
            value={formData.email || ''}
            onChange={handleEmailChange}
            className="flex-grow"
          />
          <Button
            onClick={verifyEmail}
            disabled={!formData.email || isVerifying}
            className="self-end h-full w-[190px] cursor-pointer bg-violet-500 text-white hover:bg-violet-800"
          >
            {isVerifying ? 'Verificando...' : 'Verificar Email'}
          </Button>
        </div>
        <Input
          label="Name"
          name="name"
          value={formData.name || ''}
          onChange={handleInputChange}
          isDisabled={!isEmailValidated}
        />
        <Input
          label="Last Name"
          name="last_name"
          value={formData.last_name || ''}
          onChange={handleInputChange}
          isDisabled={!isEmailValidated}
        />
        <Input
          label="Phone Number"
          name="phone_number"
          value={formData.phone_number || ''}
          onChange={handleInputChange}
          isDisabled={!isEmailValidated}
        />
        <AskerAddressComponent
          ref={askerAddressComponentRef}
          askerAddresses={askerAddresses}
          isEmailValidated={isEmailValidated}
          onAddressChange={handleAddressChange}
          askerId={formData.asker_id || null}
        />
      </div>
    );
  }
);

export default UserInfoColumn;
